import { useState, useCallback, useEffect } from "react";

export const useInstructor = (userId, userRole) => {
  const [enrollmentNotifications, setEnrollmentNotifications] = useState([]);
  const [enrollmentNotificationCount, setEnrollmentNotificationCount] = useState(0);
  const [isLoadingEnrollmentNotifications, setIsLoadingEnrollmentNotifications] = useState(false);
  const API_URL = process.env.NEXT_PUBLIC_API_URL;

  const getUnreadEntryWeight = useCallback((notification) => {
    if (!notification || notification.isRead) return 0;
    if (Number.isFinite(notification.pendingCount) && notification.pendingCount > 0) {
      return notification.pendingCount;
    }
    return 1;
  }, []);

  const fetchEnrollmentNotifications = useCallback(async () => {
    if (!userId || userRole !== 'instructor') return;
    
    try {
      setIsLoadingEnrollmentNotifications(true);
      
      const response = await fetch(
        `${API_URL}/notifications?type=EVENT_ENROLLMENT,COURSE_ENROLLMENT&limit=50&page=1`,
        { method: "GET", credentials: "include" }
      );
      
      if (response.ok) {
        const result = await response.json();
        const notifications = result.data || [];
        
        setEnrollmentNotifications(notifications);
        
        const unreadCount = notifications.reduce((sum, n) => sum + getUnreadEntryWeight(n), 0);
        setEnrollmentNotificationCount(unreadCount);
      } else {
        setEnrollmentNotifications([]);
        setEnrollmentNotificationCount(0);
      }
    } catch (error) {
      console.error("Error fetching enrollment notifications:", error);
      setEnrollmentNotifications([]);
      setEnrollmentNotificationCount(0);
    } finally {
      setIsLoadingEnrollmentNotifications(false);
    }
  }, [userId, userRole, API_URL, getUnreadEntryWeight]);

  const markEnrollmentNotificationAsRead = useCallback(async (notificationId) => {
    if (!notificationId || !userId) return;
    
    try {
      const response = await fetch(
        `${API_URL}/notifications/${notificationId}/read`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );
      
      if (response.ok) {
        const previousNotification = enrollmentNotifications.find(n => n._id === notificationId);
        const previousWeight = getUnreadEntryWeight(previousNotification);

        setEnrollmentNotifications(prev => 
          prev.map(n => 
            n._id === notificationId ? { ...n, isRead: true } : n
          )
        );
        
        setEnrollmentNotificationCount(prev => Math.max(0, prev - previousWeight));
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  }, [userId, API_URL, enrollmentNotifications, getUnreadEntryWeight]);

  const markAllEnrollmentNotificationsAsRead = useCallback(async () => {
    if (!userId || userRole !== 'instructor') return;
    
    try {
      const response = await fetch(
        `${API_URL}/notifications/mark-all-read`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );
      
      if (response.ok) {
        setEnrollmentNotifications(prev => 
          prev.map(n => ({ ...n, isRead: true }))
        );
        setEnrollmentNotificationCount(0);
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  }, [userId, userRole, API_URL]);

  useEffect(() => {
    if (userId && userRole === 'instructor') {
      fetchEnrollmentNotifications();
    }
  }, [userId, userRole, fetchEnrollmentNotifications]);

  return {
    enrollmentNotifications,
    enrollmentNotificationCount,
    isLoadingEnrollmentNotifications,
    fetchEnrollmentNotifications,
    markEnrollmentNotificationAsRead,
    markAllEnrollmentNotificationsAsRead,
  };
};
